import { atom } from 'nanostores';
import type { AppBundle } from '@/utils/app-bundles';
import { getAppBundleFromUrl } from '@/utils/navigation';
import { accountStore } from '@/services/auth/store';

/**
 * A contextual "Send us feedback" affordance registered by a surface (a hydra-native
 * page or an embedded micro-frontend). The shell renders every registered button in a
 * single consistent location in the top navigation. Clicking it opens the shared
 * feedback modal via `useCustomerFeedback`, so every surface hits the same endpoint
 * (`POST /v1/accounts/:account_id/feedback`) with the same payload shape.
 */
export interface FeedbackButton {
  /** Button label, e.g. "Send us feedback". */
  label: string;
  /**
   * Feature name routed to the modal. When set, the modal is the single-textarea
   * "{feature} Feedback" variant (source becomes `"{feature} Feedback"`). When omitted,
   * the general criticism/praise modal is shown (source `"General Feedback"`).
   */
  feature?: string;
  /** Optional prefilled feedback text. */
  initialFeedback?: string;
  /**
   * Optional feature-specific message shown in the modal body, e.g.
   * "Tell us how audit logs are working for you." Falls back to a generated
   * sentence when omitted.
   */
  message?: string;
  /** Optional icon name to show next to the label. */
  icon?: string;
}

interface ProviderEntry extends FeedbackButton {
  app: AppBundle | null;
  providerId: string;
}

let nextRegistrationId = 0;

export const feedbackButtonsAtom = atom<Map<string, ProviderEntry>>(new Map());

function unregister(key: string): void {
  const current = new Map(feedbackButtonsAtom.get());
  current.delete(key);
  feedbackButtonsAtom.set(current);
}

/**
 * Register a contextual feedback button for the current app. Returns a cleanup function
 * that removes the registration. Follows the same app-scoped, dedupe-by-providerId
 * semantics as `window.__hydra_commands__.register`.
 */
export function register(providerId: string, button: FeedbackButton): () => void {
  const id = nextRegistrationId++;
  const key = `${id}-${providerId}`;
  const app = getAppBundleFromUrl(window.location.pathname, accountStore.get());
  const entry: ProviderEntry = { ...button, app, providerId };

  const current = new Map(feedbackButtonsAtom.get());
  // Replace only an existing registration for this providerId in this app, so that
  // re-registering (e.g. updating the label) doesn't duplicate, but a different app
  // using the same providerId does not remove the other app's button.
  const keysToDelete = [...current.entries()]
    .filter(([, existing]) => existing.providerId === providerId && existing.app === app)
    .map(([k]) => k);

  for (const k of keysToDelete) {
    current.delete(k);
  }

  current.set(key, entry);
  feedbackButtonsAtom.set(current);

  return () => unregister(key);
}

// Expose to window so embedded micro-frontends (Design Studio, the Ember app) can
// register a feedback button without importing hydra internals. Follows the
// __hydra_commands__ / __hydra_sidebar__ bridge pattern.
Object.assign(window, {
  __hydra_feedback__: {
    register,
  } satisfies Window['__hydra_feedback__'],
});
